import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

export class RangeBasedGLTFLoader {
    constructor(manager) {
        this.manager = manager;
        this.gltfLoader = new GLTFLoader(manager);
    }

    async load(url, onLoad, onProgress, onError) {
        try {
            console.log(
                "RangeBasedGLTFLoader: Starting range-based load for:",
                url,
            );

            if (url.includes("/api/stream_model/")) {
                await this.loadWithRangeRequests(
                    url,
                    onLoad,
                    onProgress,
                    onError,
                );
            } else {
                this.gltfLoader.load(
                    url,
                    (gltf) => {
                        const scene = gltf.scene;
                        scene.animations = gltf.animations || [];
                        console.log(
                            "RangeBasedGLTFLoader: GLB loaded successfully",
                        );
                        if (onLoad) onLoad(scene);
                    },
                    onProgress,
                    onError,
                );
            }
        } catch (error) {
            console.error("RangeBasedGLTFLoader: Load failed:", error);
            if (onError) onError(error);
        }
    }

    async loadWithRangeRequests(url, onLoad, onProgress, onError) {
        try {
            console.log(
                "RangeBasedGLTFLoader: Using range-based loading for:",
                url,
            );

            // Consume the in-flight first-byte fetch the lab page started
            // during HTML head parse, if it matches this URL. Otherwise
            // issue the size probe ourselves.
            let sizeResponse;
            const preload =
                typeof window !== "undefined" ? window.__modelPreload : null;
            if (preload && preload.url === url && preload.promise) {
                window.__modelPreload = null;
                try {
                    sizeResponse = await preload.promise;
                } catch (_) {
                    sizeResponse = null;
                }
            }
            if (!sizeResponse) {
                sizeResponse = await fetch(url, {
                    method: "GET",
                    headers: { Range: "bytes=0-0" },
                });
            }

            if (typeof window !== "undefined" && window.__characterClickAt) {
                const elapsed =
                    performance.timeOrigin + performance.now() -
                    window.__characterClickAt;
                console.log(
                    "[Perf] Click → first GLB byte:",
                    Math.round(elapsed),
                    "ms",
                );
                window.__characterClickAt = 0;
            }

            if (!sizeResponse.ok) {
                throw new Error(
                    `Failed to get file size: ${sizeResponse.status}`,
                );
            }

            const contentRange = sizeResponse.headers.get("Content-Range");
            if (!contentRange) {
                throw new Error("Server does not support range requests");
            }

            const totalSize = parseInt(contentRange.split("/")[1]);
            console.log(`RangeBasedGLTFLoader: File size: ${totalSize} bytes`);

            const resolvedUrl = sizeResponse.url || url;
            if (resolvedUrl !== url) {
                console.log(
                    "RangeBasedGLTFLoader: Resolved URL after redirect:",
                    resolvedUrl,
                );
            }

            const chunkSize = 4 * 1024 * 1024;
            const chunks = [];
            let downloaded = 0;

            for (let start = 0; start < totalSize; start += chunkSize) {
                const end = Math.min(start + chunkSize - 1, totalSize - 1);

                console.log(
                    `RangeBasedGLTFLoader: Downloading chunk ${start}-${end}`,
                );

                const chunkResponse = await fetch(resolvedUrl, {
                    method: "GET",
                    headers: {
                        Range: `bytes=${start}-${end}`,
                    },
                });

                if (!chunkResponse.ok) {
                    throw new Error(
                        `Chunk download failed: ${chunkResponse.status}`,
                    );
                }

                const chunkData = await chunkResponse.arrayBuffer();
                chunks.push(chunkData);
                downloaded += chunkData.byteLength;

                if (onProgress) {
                    onProgress({
                        lengthComputable: true,
                        loaded: downloaded,
                        total: totalSize,
                    });
                }
            }

            console.log("RangeBasedGLTFLoader: Creating blob from chunks...");

            // Verify the first 4 bytes are the GLB magic 'glTF' before parsing
            if (chunks.length > 0) {
                const firstBytes = new Uint8Array(chunks[0], 0, Math.min(4, chunks[0].byteLength));
                const magic = String.fromCharCode(...firstBytes);
                if (magic !== "glTF") {
                    throw new Error(
                        `Downloaded data is not a valid GLB file. ` +
                        `Expected 'glTF' magic bytes but got '${magic}'. ` +
                        `This may indicate a browser cache issue — try a hard refresh (Ctrl+Shift+R).`
                    );
                }
                console.log("RangeBasedGLTFLoader: GLB magic bytes verified ✓");
            }

            const blob = new Blob(chunks, { type: "model/gltf-binary" });
            chunks.length = 0;

            const blobUrl = URL.createObjectURL(blob);

            console.log("RangeBasedGLTFLoader: Loading GLB from blob URL...");

            this.gltfLoader.load(
                blobUrl,
                (gltf) => {
                    URL.revokeObjectURL(blobUrl);
                    const scene = gltf.scene;
                    scene.animations = gltf.animations || [];
                    console.log(
                        "RangeBasedGLTFLoader: GLB loaded successfully",
                    );
                    if (onLoad) onLoad(scene);
                },
                (progress) => {
                    console.log(
                        "RangeBasedGLTFLoader: GLB parsing progress:",
                        progress,
                    );
                },
                (error) => {
                    URL.revokeObjectURL(blobUrl);
                    console.error(
                        "RangeBasedGLTFLoader: GLB parsing failed:",
                        error,
                    );
                    if (onError) onError(error);
                },
            );
        } catch (error) {
            console.error(
                "RangeBasedGLTFLoader: Range-based loading failed:",
                error,
            );
            if (onError) onError(error);
        }
    }
}
